import { useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { useTracker } from '@/lib/tracker';
import {
  PARTIES,
  RAID_TYPES,
  SEVERITIES,
  TYPE_LABEL,
  TYPE_PLURAL,
  bandOf,
  isOpen,
  partyLabel,
  raidKey,
  useOpenRaid,
  useRaid,
  type RaidType,
  type Severity,
} from '@/lib/raid';
import type { Program } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/field';
import { Card, PageHeader, Spinner } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { HeatMap, RaidTable } from '@/components/raid/RaidTable';
import { SuggestRisks } from '@/components/ai/SuggestRisks';

type Tab = 'ALL' | RaidType;
type Show = 'open' | 'closed' | 'all';

export function RaidPage() {
  const program = useOutletContext<Program>();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('type') as Tab) || 'ALL';
  const [show, setShow] = useState<Show>('open');
  const [phaseId, setPhaseId] = useState('');
  const [cell, setCell] = useState<{ l: number; i: number } | null>(null);
  const [raising, setRaising] = useState(false);
  const q = useRaid(program.id);
  const tracker = useTracker(program.id);
  const canRaise = program.membership.role === 'ADMIN' || program.membership.role === 'CONTRIBUTOR';

  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    if (t === 'ALL') next.delete('type');
    else next.set('type', t);
    setParams(next, { replace: true });
    setCell(null);
  };

  if (!q.data) return <Spinner className="h-64" />;
  const all = q.data;
  const base = all.filter(
    (i) =>
      (show === 'all' || (show === 'open' ? isOpen(i) : !isOpen(i))) &&
      (!phaseId || (phaseId === 'program' ? !i.phaseId : i.phaseId === phaseId)),
  );
  const items = base
    .filter((i) => tab === 'ALL' || i.type === tab)
    .filter((i) => !cell || (i.type === 'RISK' && i.likelihood === cell.l && i.impact === cell.i))
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0) || a.type.localeCompare(b.type) || a.seq - b.seq);
  const openRisks = all.filter((i) => i.type === 'RISK' && isOpen(i));
  const high = openRisks.filter((r) => r.score && bandOf(r.score) === 'HIGH').length;

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-6">
      <PageHeader
        title="RAID log"
        description="Risks, assumptions, issues, decisions and dependencies."
        actions={
          <>
            {canRaise && <SuggestRisks program={program} />}
            {canRaise && (
              <Button onClick={() => setRaising(true)}>
                <Plus /> Raise item
              </Button>
            )}
          </>
        }
      />

      {(tab === 'ALL' || tab === 'RISK') && (
        <Card className="mb-5 flex flex-wrap items-center gap-8 p-5">
          <HeatMap items={all} selected={cell} onSelect={(c) => (setCell(c), c && tab === 'ALL' && setTab('RISK'))} />
          <div className="space-y-2 text-sm">
            <p>
              <span className="text-2xl font-semibold tabular-nums">{openRisks.length}</span>{' '}
              <span className="text-muted">open risk{openRisks.length === 1 ? '' : 's'}</span>
            </p>
            <p className={cn(high ? 'text-bad' : 'text-muted')}>{high} high (score 15 or more)</p>
            <p className="max-w-xs text-xs text-muted">
              Score is likelihood times impact. Bands are a stated default: 1 to 6 low, 8 to 12 medium, 15 to 25 high.
              Click a cell to filter the list.
            </p>
            {cell && (
              <Button variant="secondary" size="sm" onClick={() => setCell(null)}>
                <X /> Likelihood {cell.l}, impact {cell.i}
              </Button>
            )}
          </div>
        </Card>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 border-b border-border" role="tablist">
          {(['ALL', ...RAID_TYPES] as Tab[]).map((t) => {
            const n = base.filter((i) => t === 'ALL' || i.type === t).length;
            return (
              <button
                key={t}
                role="tab"
                aria-selected={tab === t}
                type="button"
                onClick={() => setTab(t)}
                className={cn(
                  '-mb-px border-b-2 px-3 pb-2 pt-1 text-sm',
                  tab === t ? 'border-brand font-medium text-text' : 'border-transparent text-muted hover:text-text',
                )}
              >
                {t === 'ALL' ? 'All' : TYPE_PLURAL[t]} <span className="text-xs text-muted">{n}</span>
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex gap-2">
          <Select value={show} onChange={(e) => setShow(e.target.value as Show)} aria-label="Status" className="w-32">
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </Select>
          <Select value={phaseId} onChange={(e) => setPhaseId(e.target.value)} aria-label="Phase" className="w-48">
            <option value="">All phases</option>
            <option value="program">Program-wide</option>
            {tracker.data?.phases.map((p) => (
              <option key={p.id} value={p.id}>
                {p.number}. {p.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <Card className="p-0">
        <RaidTable program={program} items={items} empty={cell ? 'No open risks in that cell.' : 'Nothing here.'} />
      </Card>

      <RaiseDialog
        key={tab}
        program={program}
        open={raising}
        onClose={() => setRaising(false)}
        initialType={tab === 'ALL' ? 'RISK' : tab}
      />
    </div>
  );
}

function RaiseDialog({
  program,
  open,
  onClose,
  initialType,
}: {
  program: Program;
  open: boolean;
  onClose: () => void;
  initialType: RaidType;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const openItem = useOpenRaid();
  const tracker = useTracker(program.id);
  const admin = program.membership.role === 'ADMIN';
  const blank = {
    title: '',
    description: '',
    phaseId: '',
    targetDate: '',
    likelihood: 3,
    impact: 3,
    severity: 'MEDIUM' as Severity,
    ownerParty: 'CONSULTANT' as (typeof PARTIES)[number],
    options: '',
  };
  const [type, setType] = useState<RaidType>(initialType);
  const [f, setF] = useState(blank);
  const set = (p: Partial<typeof blank>) => setF((x) => ({ ...x, ...p }));

  const raise = useMutation({
    mutationFn: () =>
      api.post<{ id: string; ref: string }>(`/api/programs/${program.id}/raid`, {
        type,
        title: f.title,
        description: f.description || null,
        phaseId: f.phaseId || null,
        targetDate: f.targetDate || null,
        ...(admin ? { ownerParty: f.ownerParty } : {}),
        ...(type === 'RISK' ? { likelihood: f.likelihood, impact: f.impact } : {}),
        ...(type === 'ISSUE' ? { severity: f.severity } : {}),
        ...(type === 'DECISION' && f.options.trim()
          ? {
              options: f.options
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean)
                .map((label) => ({ label })),
            }
          : {}),
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: raidKey(program.id) });
      void qc.invalidateQueries({ queryKey: ['tracker', program.id] });
      toast.ok(`${r.ref} raised.`);
      setF(blank);
      onClose();
      openItem(r.id);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const score = f.likelihood * f.impact;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Raise a RAID item"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => raise.mutate()} loading={raise.isPending} disabled={!f.title.trim()}>
            Raise {TYPE_LABEL[type].toLowerCase()}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Type">
          {RAID_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={type === t}
              onClick={() => setType(t)}
              className={cn(
                'rounded-full border px-3 py-1 text-[13px]',
                type === t ? 'border-brand bg-brand-soft text-brand-text' : 'border-border text-muted hover:text-text',
              )}
            >
              {TYPE_LABEL[t]}
            </button>
          ))}
        </div>
        <Field label="Title" htmlFor="raid-title">
          <Input
            id="raid-title"
            value={f.title}
            onChange={(e) => set({ title: e.target.value })}
            maxLength={200}
            autoFocus
          />
        </Field>
        <Field label="Description" htmlFor="raid-desc">
          <Textarea
            id="raid-desc"
            rows={3}
            value={f.description}
            onChange={(e) => set({ description: e.target.value })}
          />
        </Field>
        {type === 'RISK' && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Likelihood (1 to 5)" htmlFor="raid-l">
              <Select id="raid-l" value={f.likelihood} onChange={(e) => set({ likelihood: Number(e.target.value) })}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n}>{n}</option>
                ))}
              </Select>
            </Field>
            <Field label="Impact (1 to 5)" htmlFor="raid-i" hint={`Score ${score}, ${bandOf(score).toLowerCase()}.`}>
              <Select id="raid-i" value={f.impact} onChange={(e) => set({ impact: Number(e.target.value) })}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n}>{n}</option>
                ))}
              </Select>
            </Field>
          </div>
        )}
        {type === 'ISSUE' && (
          <Field label="Severity" htmlFor="raid-sev">
            <Select id="raid-sev" value={f.severity} onChange={(e) => set({ severity: e.target.value as Severity })}>
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s.toLowerCase()}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {type === 'DECISION' && (
          <Field label="Options" htmlFor="raid-opts" hint="One per line. Optional.">
            <Textarea id="raid-opts" rows={3} value={f.options} onChange={(e) => set({ options: e.target.value })} />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phase" htmlFor="raid-phase">
            <Select id="raid-phase" value={f.phaseId} onChange={(e) => set({ phaseId: e.target.value })}>
              <option value="">Program-wide</option>
              {tracker.data?.phases.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.number}. {p.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={type === 'DECISION' ? 'Needed by' : 'Target date'} htmlFor="raid-date">
            <Input
              id="raid-date"
              type="date"
              value={f.targetDate}
              onChange={(e) => set({ targetDate: e.target.value })}
            />
          </Field>
        </div>
        {admin && (
          <Field label="Owner" htmlFor="raid-owner">
            <Select
              id="raid-owner"
              value={f.ownerParty}
              onChange={(e) => set({ ownerParty: e.target.value as (typeof PARTIES)[number] })}
            >
              {PARTIES.map((p) => (
                <option key={p} value={p}>
                  {partyLabel(p, program)}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>
    </Dialog>
  );
}
